import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Theme, Tooltip, useMediaQuery } from "@mui/material";
import {
    List,
    Datagrid,
    TextField as RaTextField,
    TopToolbar,
    CreateButton,
    DateField,
    FunctionField,
    EditButton,
    Button,
    TextInput,
    DeleteButton,
    useNotify,
    useRefresh,
    useDataProvider,
    Form,
    PasswordInput,
    useRecordContext,
    required
} from "react-admin";
import DeleteIcon from '@mui/icons-material/Delete'; // 删除图标
import { useState } from 'react';
import PasswordVerifiers from "../deploy/passwordVerifiers";

const postFilters: any = [
    <TextInput
        alwaysOn
        key="searchKeyword"
        source="searchKeyword"
        label="姓名"
    />
];

const ListActions = () => {
    return (
        <TopToolbar>
            <CreateButton />
        </TopToolbar>
    );
};

export const DeleteWithConfirmButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    const handleDeleteClick = () => {
        setPasswordDialogOpen(true);
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialogOpen(false);
    };

    const handleDelete = () => {
        setLoading(true);
        dataProvider.delete('userManage', { id: record.id })
            .then(() => {
                notify(`账号 ${record.name} 已删除`, { type: 'success' });
                refresh();
            })
            .catch(error => {
                notify(`删除失败: ${error.message}`, { type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    // 小屏幕使用图标按钮
    if (isSmallScreen) {
        return (
            <>
                <Tooltip title="删除" arrow>
                    <IconButton
                        onClick={handleDeleteClick}
                        disabled={loading}
                        color="error"
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
                <PasswordVerifiers
                    open={passwordDialogOpen}
                    onClose={handleClosePasswordDialog}
                    onConfirm={handleDelete}
                />
            </>
        );
    }

    // 大屏幕使用带图标的文字按钮
    return (
        <>
            <Button
                label="删除"
                onClick={handleDeleteClick}
                disabled={loading}
                color="error"
                startIcon={<DeleteIcon />}
                sx={{
                    fontSize: '14px',
                    padding: '6px 12px',
                    minWidth: 'auto',
                }}
            />
            <PasswordVerifiers
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                onConfirm={handleDelete}
            />
        </>
    );
};

// 添加密码校验
const validatePassword = (value: string) => {
    if (!value) {
        return '密码是必填项';
    }


    // 检查是否包含中文或全角字符
    const hasChineseOrFullWidth = /[\u4e00-\u9fa5\uFF00-\uFFFF]/.test(value);
    if (hasChineseOrFullWidth) {
        return '密码只能包含字母、数字以及英文标点符号';
    }

    // 检查长度
    if (value.length < 8 || value.length > 32) {
        return '密码长度应为8-32个字符之间';
    }


    return undefined;
};

// 重置密码按钮组件
const ResetPasswordButton = ({ record }: { record: any }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };

    const handleResetPassword = async (data: any) => {
        if (data.password !== data.confirmPassword) {
            notify('两次输入的密码不一致', { type: 'error' });
            return;
        }

        setLoading(true);
        try {
            await dataProvider.resetPassword('userManage', {
                data: {
                    userId: record.id,
                    password: data.password
                }
            });
            notify('密码重置成功', { type: 'success' });
            handleClose();
            refresh(); // 刷新列表
        } catch (error: any) {
            notify(`重置密码失败: ${error.message}`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                color="primary"
                onClick={handleOpen}
                size="small"
            >
                重置密码
            </Button>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>重置密码 - {record.username}</DialogTitle>
                <Form onSubmit={handleResetPassword}>
                    <DialogContent>
                        <PasswordInput
                            autoFocus
                            margin="dense"
                            label="新密码"
                            validate={[required(), validatePassword]}
                            source="password"
                            fullWidth
                            variant="standard"
                            disabled={loading}
                        />
                        <PasswordInput
                            margin="dense"
                            label="确认密码"
                            source="confirmPassword"
                            fullWidth
                            variant="standard"
                            disabled={loading}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={loading}>取消</Button>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={loading}
                        >
                            {loading ? '处理中...' : '确定重置'}
                        </Button>
                    </DialogActions>
                </Form>
            </Dialog>
        </>
    );
};

export const userManageList = () => {
    return (
        <List filters={postFilters} actions={<ListActions />} empty={false} >
            <Datagrid bulkActionButtons={false} rowClick={false}>
                <RaTextField sortable={false} label="ID" source="id" />
                <RaTextField sortable={false} label="角色名称" source="roleName" />
                <RaTextField sortable={false} label="角色代码" source="roleCode" />
                <RaTextField sortable={false} label="用户名" source="username" />
                <RaTextField sortable={false} label="姓名" source="name" />
                <RaTextField sortable={false} label="联系电话" source="phone" />
                <DateField sortable={false} label="创建日期" source="createTime" />
                <FunctionField
                    label="操作"
                    render={(record) => (
                        <Box sx={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
                            <EditButton />
                            <ResetPasswordButton record={record} />
                            <DeleteWithConfirmButton />
                        </Box>
                    )}
                />
            </Datagrid>
        </List>
    )
}
